import { Component, forwardRef, inject } from '@angular/core';
import { combineLatest, map, mergeMap, Observable, switchMap } from 'rxjs';
import { Rol, rollen, validatedProfielId } from 'parkour-web-app-dto';
import { TeamService } from '../../service/team.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { AsyncPipe, Location } from '@angular/common';
import { ProfielDetailsComponent } from '../../../profiel/component/profile-details/profiel-details.component';
import { ContextService } from '../../../shared/services/context.service';
import { stripNullProperties } from '../../../utils';
import { TeamlidProfiel } from '../../model/teamlid.model';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { ProfielnaamPipe } from '../../../shared/pipes/profielnaam.pipe';
import { ParkourBackButtonDirective } from '../../../shared/directives/parkour-back-button.directive';
import { ParkourHeaderActionComponent } from '../../../shared/components/parkour-header-action/parkour-header-action.component';
import { TeamlidDetailsEditBottomSectionComponent } from '../../component/teamlid-details-edit-bottom-section/teamlid-details-edit-bottom-section.component';
import { PageWithGradientHeroComponent } from '../../../shared/components/page-with-gradient-hero/page-with-gradient-hero.component';
import { ProfielFotoComponent } from '../../../shared/components/profiel-foto/profiel-foto.component';
import {
  ParkourButtonComponent,
  ParkourFormFieldComponent,
  ParkourPopupComponent,
  ParkourSelectComponent,
} from '@parkour/ui';
import { ShowIfSuccesfulDirective } from '../../../shared/directives/show-if-succesful.directive';
import { PageLayoutComponent } from '../../../shared/components/page-layout/page-layout.component';
import { PageContentDirective } from '../../../shared/directives/page-content.directive';
import { getProfielnaam } from '../../../profiel/model/profiel-in-team';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { getRolLabel } from '../../../profiel/rol-labels';
import { ParkourDataPage } from '../../../shared/parkour-data-page';
import { CustomPageTitle, PageTitle } from '../../../shared/custom-page-title';
import { ProfielDetailsGeverifieerdCardComponent } from '../../../profiel/component/profiel-details-geverifieerd-card/profiel-details-geverifieerd-card.component';

@Component({
  standalone: true,
  templateUrl: './teamlid-details-edit.page.html',
  imports: [
    ParkourBackButtonDirective,
    ParkourHeaderActionComponent,
    TranslateModule,
    AsyncPipe,
    TeamlidDetailsEditBottomSectionComponent,
    PageWithGradientHeroComponent,
    ProfielnaamPipe,
    ProfielFotoComponent,
    ParkourPopupComponent,
    ParkourButtonComponent,
    ShowIfSuccesfulDirective,
    PageLayoutComponent,
    forwardRef(() => PageContentDirective),
    ProfielDetailsComponent,
    IonBackButton,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonContent,
    FormsModule,
    ParkourFormFieldComponent,
    ParkourSelectComponent,
    ProfielDetailsGeverifieerdCardComponent,
    ReactiveFormsModule,
  ],
})
export class TeamlidDetailsEditPage
  extends ParkourDataPage<{
    teamlid: TeamlidProfiel;
  }>
  implements CustomPageTitle
{
  isVerwijderTeamlidPopupOpen = false;
  isActiveerTeamlidPopupOpen = false;
  isBlokkeerTeamlidPopupOpen = false;

  rolOptions = Object.entries(rollen).map(([, value]) => ({
    value: value,
    label: getRolLabel(value),
  }));

  private readonly route = inject(ActivatedRoute);
  private readonly teamService = inject(TeamService);
  private readonly translate = inject(TranslatePipe);
  private readonly formBuilder = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly contextService = inject(ContextService);

  override retrieveMainData(): Observable<{ teamlid: TeamlidProfiel }> {
    const id = this.getTeamlidId();

    return combineLatest({
      teamlid: this.teamService.getTeamlidInCurrentContext(id),
    });
  }

  private getTeamlidId() {
    return validatedProfielId(this.route.snapshot.paramMap.get('teamlidId'));
  }

  getCustomPageTitle(): Observable<PageTitle> {
    return this.mainPageData$.pipe(
      map((result) => {
        if (result.success) {
          return {
            title: getProfielnaam(result.value.teamlid),
            detail: this.translate.transform('team.teamlid-details-edit-page.title'),
          };
        } else {
          return this.translate.transform('team.teamlid-details-edit-page.title');
        }
      }),
    );
  }

  protected readonly detailsEditForm$ = this.mainPageData$.pipe(
    map((data) => {
      if (data.success && data.value.teamlid.status === 'ACTIEF') {
        return this.formBuilder.nonNullable.group({
          rol: this.formBuilder.nonNullable.control<Rol>(data.value.teamlid.rol),
        });
      }
      return undefined;
    }),
  );

  async verwijderPressed() {
    this.isVerwijderTeamlidPopupOpen = false;

    this.contextService
      .contextIdOfJongere$()
      .pipe(
        mergeMap((contextId) =>
          this.teamService.removeTeamlid(contextId, this.getTeamlidId(), 'verwijderd'),
        ),
      )
      .subscribe(() => this.router.navigate(['../../../'], { relativeTo: this.route }));
  }

  async activeer() {
    this.isActiveerTeamlidPopupOpen = false;

    this.contextService
      .contextIdOfJongere$()
      .pipe(
        switchMap((contextId) => this.teamService.activeerTeamlid(contextId, this.getTeamlidId())),
      )
      .subscribe(() => this.refreshMainData());
  }

  async blokkeer() {
    this.isBlokkeerTeamlidPopupOpen = false;

    this.contextService
      .contextIdOfJongere$()
      .pipe(
        switchMap((contextId) => this.teamService.blokkeerTeamlid(contextId, this.getTeamlidId())),
      )
      .subscribe(() => {
        this.refreshMainData();
      });
  }

  onDetailsSubmit(
    detailsEditForm: FormGroup<{
      rol: FormControl<Rol>;
    }>,
  ) {
    this.contextService
      .contextIdOfJongere$()
      .pipe(
        mergeMap((contextId) =>
          this.teamService.updateTeamlid(
            contextId,
            this.getTeamlidId(),
            stripNullProperties({
              rol: detailsEditForm.value.rol,
            }),
          ),
        ),
      )
      .subscribe(() => this.location.back());
  }
}
